import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { challengeAPI } from '@/utils/api';
import { useLang } from '@/utils/i18n';
import { toast } from 'sonner';
import {
  ArrowLeft, Swords, Clock, Check, X, Trophy, Send, Shield, Flame,
} from 'lucide-react';
import LangSwitcher from '@/components/LangSwitcher';
import { MobileBottomNav } from '@/components/MobileNav';

const ChallengePage = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [tab, setTab] = useState('active');
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedType, setSelectedType] = useState('standard');

  const TYPES = [
    { id: 'blitz', name: 'Blitz', duration: '24h', bonus: 3, icon: '⚡', desc: '24-Stunden Sprint' },
    { id: 'standard', name: 'Standard', duration: '7 Tage', bonus: 5, icon: '⚔️', desc: '7-Tage Klassiker' },
    { id: 'marathon', name: 'Marathon', duration: '30 Tage', bonus: 15, icon: '🏆', desc: '30-Tage Marathon' },
    { id: 'highstakes', name: 'High Stakes', duration: '7 Tage', bonus: 25, icon: '💎', desc: '25 Bonus-Tipps!' },
  ];

  useEffect(() => {
    loadChallenges();
  }, [tab]);

  const loadChallenges = async () => {
    setLoading(true);
    try {
      const res = await challengeAPI.getMy(tab === 'all' ? '' : tab);
      setChallenges(res.data.challenges || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!code.trim()) return;
    setSending(true);
    try {
      const res = await challengeAPI.create(code.trim(), selectedType);
      toast.success(res.data.message);
      setCode('');
      loadChallenges();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
    setSending(false);
  };

  const handleRespond = async (id, accept) => {
    try {
      const res = await challengeAPI.respond(id, accept);
      toast.success(res.data.message);
      loadChallenges();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const statusBadge = (status, amWinner) => {
    const map = {
      pending: { text: 'WARTEND', cls: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' },
      active: { text: 'AKTIV', cls: 'text-green-400 bg-green-500/10 border-green-500/30' },
      completed: amWinner
        ? { text: 'GEWONNEN', cls: 'text-[#00BFA6] bg-[#00BFA6]/10 border-[#00BFA6]/30' }
        : amWinner === false
        ? { text: 'VERLOREN', cls: 'text-red-400 bg-red-500/10 border-red-500/30' }
        : { text: 'UNENTSCHIEDEN', cls: 'text-gray-400 bg-gray-500/10 border-gray-500/30' },
      declined: { text: 'ABGELEHNT', cls: 'text-gray-500 bg-gray-600/10 border-gray-600/30' },
    };
    const s = map[status] || map.pending;
    return (
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${s.cls}`}>
        {s.text}
      </span>
    );
  };

  const timeLeft = (endsAt) => {
    if (!endsAt) return '';
    try {
      const diff = new Date(endsAt) - Date.now();
      if (diff <= 0) return 'Beendet';
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      return `${d}d ${h}h`;
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] pb-16 md:pb-0" data-testid="challenge-page">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="challenge-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
          </Button>
          <h1 className="text-lg font-bold text-[#00BFA6] flex items-center gap-2">
            <Swords className="w-5 h-5" /> {t('challenges')}
          </h1>
          <LangSwitcher />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-6">
        {/* Create Challenge */}
        <Card className="bg-gradient-to-r from-[#7B1FA2]/20 to-[#4A148C]/10 border-[#7B1FA2]/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Swords className="w-5 h-5 text-[#7B1FA2]" /> {t('create_challenge')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-400 mb-3">{t('challenge_desc')}</p>
            
            {/* Challenge Type Selector */}
            <div className="grid grid-cols-2 gap-2 mb-4" data-testid="challenge-types">
              {TYPES.map((type) => (
                <button
                  key={type.id}
                  data-testid={`challenge-type-${type.id}`}
                  onClick={() => setSelectedType(type.id)}
                  className={`p-3 rounded-xl text-left border transition-all ${
                    selectedType === type.id
                      ? 'bg-[#7B1FA2]/20 border-[#7B1FA2]/50'
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{type.icon}</span>
                    <span className="text-sm font-bold text-white">{type.name}</span>
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">{type.duration} &middot; {type.bonus} Bonus-Tipps</div>
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="BS-XXXXXXXX"
                className="bg-[#121212] border-white/10"
                data-testid="challenge-code-input"
              />
              <Button
                onClick={handleCreate}
                disabled={sending || !code.trim()}
                className="bg-[#7B1FA2] hover:bg-[#9C27B0] shrink-0"
                data-testid="challenge-send-btn"
              >
                <Send className="w-4 h-4 mr-2" /> {t('challenge_send')}
              </Button>
            </div>
            <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
              <Trophy className="w-3 h-3" /> {t('challenge_prize')}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2" data-testid="challenge-tabs">
          {['active', 'pending', 'completed', 'all'].map((t_) => (
            <button
              key={t_}
              onClick={() => setTab(t_)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                tab === t_ ? 'bg-[#00BFA6] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`challenge-tab-${t_}`}
            >
              {t_ === 'active' ? t('active_label') : t_ === 'pending' ? t('pending_label') : t_ === 'completed' ? t('completed_label') : t('all_label')}
            </button>
          ))}
        </div>

        {/* Challenge List */}
        {loading ? (
          <div className="text-center py-12 text-[#00BFA6]">{t('loading')}</div>
        ) : challenges.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="no-challenges">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>{t('no_challenges')}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="challenge-list">
            {challenges.map((c) => (
              <Card key={c.id} className="bg-[#1E1E1E] border-white/10" data-testid={`challenge-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Swords className="w-4 h-4 text-[#7B1FA2]" />
                      <span className="font-medium">{t('vs')} {c.opponent_name}</span>
                    </div>
                    {statusBadge(c.status, c.am_winner)}
                  </div>

                  {/* Score comparison */}
                  {(c.status === 'active' || c.status === 'completed') && (
                    <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                      <div>
                        <div className="text-xs text-gray-500">{t('you')}</div>
                        <div className="font-mono text-lg font-bold text-[#00BFA6]">{c.my_score.won}W</div>
                        <div className="text-xs text-gray-600">{c.my_score.total_purchased} {t('tips')}</div>
                      </div>
                      <div className="flex items-center justify-center">
                        <span className="text-gray-500 text-lg font-bold">VS</span>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">{c.opponent_name.split('_')[0]}</div>
                        <div className="font-mono text-lg font-bold text-red-400">{c.their_score.won}W</div>
                        <div className="text-xs text-gray-600">{c.their_score.total_purchased} {t('tips')}</div>
                      </div>
                    </div>
                  )}

                  {/* Active timer */}
                  {c.status === 'active' && c.ends_at && (
                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                      <Clock className="w-3 h-3" /> {t('time_remaining')}: {timeLeft(c.ends_at)}
                    </div>
                  )}

                  {/* Winner badge */}
                  {c.status === 'completed' && c.am_winner && (
                    <div className="flex items-center gap-2 text-sm text-[#00BFA6] bg-[#00BFA6]/10 rounded-lg p-2">
                      <Trophy className="w-4 h-4" /> {t('you_won')} +{c.bonus_tips} {t('bonus_tips')}!
                    </div>
                  )}
                  {c.status === 'completed' && c.am_winner === false && (
                    <div className="text-sm text-red-400 bg-red-500/10 rounded-lg p-2">
                      {c.opponent_name} {t('has_won')}
                    </div>
                  )}
                  {c.status === 'completed' && c.am_winner === null && (
                    <div className="text-sm text-gray-400 bg-gray-500/10 rounded-lg p-2">
                      {t('draw_result')}
                    </div>
                  )}

                  {/* Pending — respond buttons */}
                  {c.status === 'pending' && c.role === 'opponent' && (
                    <div className="flex gap-2 mt-2">
                      <Button onClick={() => handleRespond(c.id, true)} className="flex-1 bg-[#00BFA6] hover:bg-[#009E8A] text-black" data-testid={`accept-${c.id}`}>
                        <Check className="w-4 h-4 mr-1" /> {t('accept')}
                      </Button>
                      <Button onClick={() => handleRespond(c.id, false)} variant="outline" className="flex-1 border-red-500/30 text-red-400" data-testid={`decline-${c.id}`}>
                        <X className="w-4 h-4 mr-1" /> {t('decline')}
                      </Button>
                    </div>
                  )}
                  {c.status === 'pending' && c.role === 'challenger' && (
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {t('waiting_response')}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default ChallengePage;
